import { message } from 'antd';
import { MenuItemType } from 'antd/es/menu/interface';
import ExcelJS, { TableStyleProperties } from 'exceljs';

import { RowData, TableModel } from '@suntex.fim/ui';

const HEADER_PADDING = 5;

export function buildExportMenu<T extends RowData>({
  tableModel,
  fileName,
  localize,
}: {
  tableModel: TableModel<T>;
  fileName: string;
  isSchool?: boolean;
  localize?: (value: string) => string;
}) {
  const { exportSelectedToExcel } = buildExportFunction({
    tableModel,
    fileName: `${fileName}`,
    localize,
  });

  const items: MenuItemType[] = [
    {
      label: 'Export to Excel',
      key: 'excel',
      onClick: () =>
        tableModel.getRowModel().rows.length
          ? void exportSelectedToExcel()
          : void message.error('No Data to Export'),
    },
  ];

  return items;
}

function buildExportFunction<T extends RowData>({
  tableModel,
  fileName,
  localize,
}: {
  tableModel: TableModel<T>;
  fileName: string;
  localize?: (value: string) => string;
}) {
  const exportSelectedToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('sheet1');
    const columns = tableModel.getVisibleFlatColumns();
    // Add headers
    worksheet.addTable({
      name: 'test',
      ref: 'A1',
      headerRow: true,
      totalsRow: false,
      style: {
        theme: null as unknown as TableStyleProperties['theme'],
        showRowStripes: false,
      },
      columns: columns.map((col) => {
        const columnHeader = col.columnDef.header;
        const header = typeof columnHeader === 'string' ? columnHeader : col.id;
        const name = localize?.(header) ?? header;
        return {
          name,
          filterButton: true,
          width: name.length + HEADER_PADDING,
        };
      }),
      rows: tableModel.getRowModel().rows.map((row) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        return columns.map((col) => row.getValue(col.id!) as unknown);
      }),
    });

    // Make header row bold
    worksheet.getRow(1).font = { bold: true };

    // Save workbook to a buffer
    const buffer = await workbook.xlsx.writeBuffer();

    // Create a blob from buffer
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Trigger file download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return {
    exportSelectedToExcel,
  };
}
